'use client';

import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrder } from '../../state/order/hooks/useOrder';
import { recentOrdersQueryKey } from '../../state/user/hooks/useRecentOrders';
import { orderHistoryQueryKey } from '../../state/order/hooks/useOrderHistory';
import { useResetOrder } from '../../state/order/hooks/useOrderMutation';
import { errorCode } from '../../model/errors';
import { ErrorMessage } from '../../model/errors/ErrorMessage';
import { useUser } from '../../hooks/useUser';
import styled from 'styled-components';
import { LoadingIcon } from '../loading/LoadingIcon';
import { useAddError } from '../error/ErrorContext';
import { useWebshopStateReset } from '../../hooks/useWebshopStateReset';
import { analyticsEvents } from '../../utils/analytics/analytics';
import { useOrderRestaurant } from '../../state/order/hooks/useOrderRestaurant';
import { PaymentsApiErrorCodes } from '@kotipizzagroup/kotipizza-payments-api-client';
import { getPaymentsApiErrorMessage } from '../../clients/payments-api';
import { Path } from '../../routes/components/path';
import { useRouter, useSearchParams } from 'next/navigation';
import { usePaymentWebViewHandoff } from '../shoppingCart/components/paymentMethods/adyen/usePaymentWebViewHandoff';
import { storage } from '../../utils/storage';

export enum PaymentStatus {
  Ok = 'OK',
  Pending = 'PENDING',
  Cancel = 'CANCEL',
}

// A given payment result must only be acted on once per session. Re-entering this dispatcher
// page for the same result (browser back/forward to a cached ?status= URL, a refresh, or a
// provider that returns here more than once) would otherwise re-add the "Maksu peruttu" banner
// and re-fire purchase analytics / resetOrder. We still redirect on re-entry so the user never
// gets stuck on the loader — only the side effects are skipped.
const RESULT_HANDLED_PREFIX = 'kotipizza.paymentResultHandled.';

export const isResultHandled = (key: string | null): boolean =>
  key !== null &&
  storage.getItem(`${RESULT_HANDLED_PREFIX}${key}`, { mode: 'sessionStorage' }) === '1';

export const markResultHandled = (key: string | null): void => {
  if (key !== null) {
    storage.setItem(`${RESULT_HANDLED_PREFIX}${key}`, '1', { mode: 'sessionStorage' });
  }
};

// Builds the per-result idempotency key. OK and PENDING share one "success" bucket so a
// PENDING→OK sequence can't double-count. Returns null when there is nothing to key on.
export const paymentResultKey = (
  status: string | undefined,
  orderId: string | null,
  orderKey: string | null
): string | null => {
  const orderRef = orderId ?? orderKey;
  const isSuccess = status === PaymentStatus.Ok || status === PaymentStatus.Pending;
  if (isSuccess) {
    return orderRef ? `success:${orderRef}` : null;
  }
  return status ? `${status}:${orderRef ?? ''}` : null;
};

export const PaymentRedirectHandler: React.FC = () => {
  const order = useOrder();
  const { deliveringRestaurant, isRestaurantLoading } = useOrderRestaurant();
  const { isCCUser, isUserLoggedIn, isUserLoading } = useUser();

  const addError = useAddError();

  const resetOrder = useResetOrder();
  const { handleResetOrderState } = useWebshopStateReset();
  const queryClient = useQueryClient();

  const params = useSearchParams();
  const router = useRouter();

  usePaymentWebViewHandoff();

  const status = params.get('status')?.toUpperCase();
  const orderId = params.get('orderid');
  const orderKey = params.get('order-key');
  // Payments API error code.
  const queryErrorCode = parseInt(params.get('errorCode') ?? '');

  useEffect(() => {
    const handlePaymentRedirect = async (): Promise<void> => {
      // Identifies this specific payment result so its side effects run at most once, even if
      // the page is re-entered (back/forward, refresh, provider returning here again).
      const resultKey = paymentResultKey(status, orderId, orderKey);

      if (status === PaymentStatus.Ok || status === PaymentStatus.Pending) {
        if (isUserLoading || isRestaurantLoading) return;

        // We should always have order id when on this page. If it's missing, something is very wrong
        if (!orderId) {
          const errorMessage = isUserLoggedIn
            ? 'Virhe tilausseurannan avaamisessa. Pääset tilausseurantaan etusivun tai tilausvahvistussähköpostin kautta.'
            : 'Virhe tilausseurannan avaamisessa. Pääset tilausseurantaan tilausvahvistussähköpostin kautta.';

          addError(
            ErrorMessage.create(errorCode.PAYMENT_REDIRECT_ERROR, errorMessage, {
              errorUrl: '/',
            })
          );

          router.replace('/');
          return;
        }

        // Mark before firing so a strict-mode/double invocation can't double-count the purchase.
        if (!isResultHandled(resultKey)) {
          markResultHandled(resultKey);

          analyticsEvents.purchase(order, orderId, deliveringRestaurant || null);

          await resetOrder();

          // The order has just been placed; the active-order caches still hold the
          // pre-order state. Invalidate them so the home page and order history refetch
          // and show the new order on their next mount instead of waiting for staleTime.
          await queryClient.invalidateQueries({ queryKey: [recentOrdersQueryKey] });
          await queryClient.invalidateQueries({ queryKey: [orderHistoryQueryKey] });
        }

        if (isCCUser) {
          handleResetOrderState();
          router.replace('/');
        } else {
          router.push(`${Path.Order}?orderId=${orderId}&orderKey=${orderKey}`);
        }
      } else if (status === PaymentStatus.Cancel) {
        if (!isResultHandled(resultKey)) {
          markResultHandled(resultKey);

          addError(
            ErrorMessage.create(errorCode.PAYMENT_CANCELLED, 'Maksu peruttu', {
              description: 'Yritä uudelleen',
              errorUrl: '/ostoskori',
            })
          );
          analyticsEvents.pushShoppingCartAction('misc.paymentCancelled');
        }
        router.replace('/ostoskori');
      } else {
        if (!isResultHandled(resultKey)) {
          markResultHandled(resultKey);

          // The provider does not always come back with an errorCode we can map, and an
          // unexplained bounce back to the cart reads as "nothing happened". Fall back to the
          // generic payment failure text so a failed payment always says so.
          const message = getPaymentsApiErrorMessage(
            queryErrorCode in PaymentsApiErrorCodes ? queryErrorCode : null
          );

          addError(
            ErrorMessage.create(errorCode.PAYMENT_ERROR, 'Maksu ei onnistunut', {
              description: message,
              errorUrl: '/ostoskori',
            })
          );
          analyticsEvents.pushShoppingCartAction('misc.paymentError', {
            ecommerce: {
              checkout_option: {
                actionField: {
                  step: 4,
                  option: 'Payment error',
                },
              },
            },
          });
        }
        router.replace('/ostoskori');
      }
    };

    handlePaymentRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoading, isRestaurantLoading]);

  return <StyledPlaceholder />;
};

export const StyledPlaceholder = styled(LoadingIcon)`
  margin: 40vh auto 50vh;
  width: 100px;
  height: 100px;
`;
